import axios from 'axios'
import type { BillingPlan, BillingInfo, SubscriptionItem } from '@/types/Billing'

export default function billing() {

  const getBillingPlans = async (): Promise<BillingPlan[]> => {
    const query = `
    query {
      Billing {
        getBillingPlans {
          id name limits
        }
      }
    }`
    try {
      const response = await axios.post('/gql', { query })
      return response.data?.data?.Billing?.getBillingPlans || []
    } catch (error) {
      console.error('Error fetching billing plans:', error)
      return []
    }
  }

  const getBillingInfo = async (vhostId: string): Promise<BillingInfo | null> => {
    const query = `
    query ($vhost: Int!) {
      Billing {
        getBillingInfo(vhost: $vhost) {
          id name vhost plan_id plan
          stripe_customer_id stripe_subscription_id
          custom_limits billingPlan defaultLimits lasttimestamp
        }
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: { vhost: Number(vhostId) },
      })
      return response.data?.data?.Billing?.getBillingInfo || null
    } catch (error) {
      console.error('Error fetching billing info:', error)
      return null
    }
  }

  const getStripeSubscriptions = async (vhostId: string): Promise<SubscriptionItem[]> => {
    const query = `
    query ($vhost: Int!) {
      Billing {
        getStripeSubscriptions(vhost: $vhost)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: { vhost: Number(vhostId) },
      })
      return response.data?.data?.Billing?.getStripeSubscriptions || []
    } catch (error) {
      console.error('Error fetching stripe subscriptions:', error)
      return []
    }
  }

  const updateBilling = async (
    vhostId: string,
    billingPlan?: number,
    customLimits?: Record<string, number>,
  ): Promise<boolean> => {
    const query = `
    mutation ($vhost: Int!, $billingPlan: Int, $customLimits: JSON) {
      BillingMutations {
        updateBilling(vhost: $vhost, billingPlan: $billingPlan, customLimits: $customLimits)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: {
          vhost: Number(vhostId),
          billingPlan: billingPlan ?? null,
          customLimits: customLimits ?? null,
        },
      })
      return response.data?.data?.BillingMutations?.updateBilling ?? false
    } catch (error) {
      console.error('Error updating billing:', error)
      return false
    }
  }

  const modifySubscriptionItem = async (
    vhostId: string,
    subItemId: string,
    newQuantity: number,
    planId: string,
  ): Promise<any> => {
    const query = `
    mutation ($vhost: Int!, $subItemId: String!, $newQuantity: Int!, $planId: String!) {
      BillingMutations {
        modifySubscriptionItem(vhost: $vhost, subItemId: $subItemId, newQuantity: $newQuantity, planId: $planId)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: {
          vhost: Number(vhostId),
          subItemId,
          newQuantity,
          planId,
        },
      })
      return response.data?.data?.BillingMutations?.modifySubscriptionItem || null
    } catch (error) {
      console.error('Error modifying subscription item:', error)
      return null
    }
  }

  const updateSubscription = async (
    vhostId: string,
    items: Array<{ id?: string; price: string; quantity: number }>,
    newLimits: Record<string, number>,
  ): Promise<any> => {
    const query = `
    mutation ($vhost: Int!, $items: JSON!, $newLimits: JSON!) {
      BillingMutations {
        updateSubscription(vhost: $vhost, items: $items, newLimits: $newLimits)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: {
          vhost: Number(vhostId),
          items,
          newLimits,
        },
      })
      return response.data?.data?.BillingMutations?.updateSubscription || null
    } catch (error) {
      console.error('Error updating subscription:', error)
      return null
    }
  }

  const stripeSubscribe = async (
    vhostId: string,
    customerID: string,
    subscription: string,
    token?: string,
  ): Promise<boolean> => {
    const query = `
    mutation ($vhost: Int!, $customerID: String!, $subscription: String!, $token: String) {
      BillingMutations {
        stripeSubscribe(vhost: $vhost, customerID: $customerID, subscription: $subscription, token: $token)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: {
          vhost: Number(vhostId),
          customerID,
          subscription,
          token: token ?? '',
        },
      })
      return response.data?.data?.BillingMutations?.stripeSubscribe ?? false
    } catch (error) {
      console.error('Error creating stripe subscription:', error)
      return false
    }
  }

  const purchase = async (
    plan: string,
    stripeToken: string,
    name: string,
    email: string,
  ): Promise<boolean> => {
    const query = `
    mutation ($plan: String!, $stripeToken: String!, $name: String!, $email: String!) {
      BillingMutations {
        purchase(plan: $plan, stripeToken: $stripeToken, name: $name, email: $email)
      }
    }`
    try {
      const response = await axios.post('/gql', {
        query,
        variables: { plan, stripeToken, name, email },
      })
      return response.data?.data?.BillingMutations?.purchase ?? false
    } catch (error) {
      console.error('Error completing purchase:', error)
      return false
    }
  }

  return {
    getBillingPlans,
    getBillingInfo,
    getStripeSubscriptions,
    updateBilling,
    modifySubscriptionItem,
    updateSubscription,
    stripeSubscribe,
    purchase,
  }
}
